import { computed, Signal } from '@angular/core';

export type LoadableStateStatus = 'loading' | 'error' | 'content';

export interface LoadableState<T> {
  loading: boolean;
  error: LoadableStateError | null;
  data: T | null;
  reload(): void;
}

export interface LoadableStateError {
  message: string;
  code?: number;
}

export interface LoadableStateContext<T> {
  $implicit: LoadableState<T>;
}

export function createLoadableStateError(message: string, code?: number): LoadableStateError {
  return { message, code };
}

export function createEmptyLoadableState<T>(): LoadableState<T> {
  return {
    loading: false,
    error: null,
    data: null,
    reload: () => {},
  };
}

export interface ToLoadableStateOptions<T> {
  data: Signal<T | null>;
  loading: Signal<boolean>;
  error?: Signal<LoadableStateError | null>;
  reload: () => void;
}

// Bundles existing store/service signals for UI rendering. The store remains the source of truth.
export function toLoadableState<T>(options: ToLoadableStateOptions<T>): Signal<LoadableState<T>> {
  return computed<LoadableState<T>>(() => ({
    loading: options.loading(),
    error: options.error?.() ?? null,
    data: options.data(),
    reload: options.reload,
  }));
}
